import { getSalesStatusChipSx } from "../utils/salesStatusColors";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import { Clock, CloseCircle, Eye, Trash } from "iconsax-react";
import ReusableRightDrawer from "./ReusableRightDrawer";
import InfoActionIcons from "./InfoActionIcons";
import {
  dedupeHiringManagerRows,
  fetchHiringManagers,
  isHiringManagerContactRow,
} from "../utils/hiringManagers";
import { formatLocalDate, parseDateTimeValue } from "../utils/dateTime";
import { formatRelativeTimeShort } from "../utils/relativeTime";
import { normalizeContactActionHref } from "../utils/contactChannels";
import { normalizeContactRoleLabel } from "../utils/contactRoles";
import { areSamePersonNames, isRecruiterContact } from "../utils/recruiterOwnership";

const API_BASE = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const BLUE = "#3451F4";
const PURPLE = "#9C4FFF";
const PURPLE_DARK = "#7C3AED";
const BORDER = "#E7ECF2";
const TEXT = "#111827";
const MUTED = "#98A2B3";
const EMPTY_VALUE = "\u2014";
const FONT_FAMILY = "Be Vietnam Pro";

function cleanText(value, fallback = "") {
  if (value === undefined || value === null) return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    ""
  );
}

function getAuthHeaders() {
  const token = cleanText(getToken());
  if (!token) return {};

  const rawToken = token.replace(/^Bearer\s+/i, "").trim();
  return {
    Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
    "x-access-token": rawToken,
  };
}

function buildLeadSnapshot(source = {}) {
  if (!source || typeof source !== "object") return {};
  const raw = source.raw && typeof source.raw === "object" ? source.raw : {};
  return {
    ...raw,
    ...source,
  };
}

function resolveLeadRecordId(source) {
  const candidates = [source, source?.raw];

  for (const item of candidates) {
    if (!item || typeof item !== "object") continue;

    for (const candidate of [item.lead_id, item.leadId, item.id, item.entity_id]) {
      const leadId = Number(candidate || 0);
      if (Number.isFinite(leadId) && leadId > 0) {
        return leadId;
      }
    }
  }

  return 0;
}

function mergeLeadState(previousLead, nextLead) {
  if (!nextLead) return previousLead || null;

  const previous = buildLeadSnapshot(previousLead);
  const next = buildLeadSnapshot(nextLead);
  const previousLeadId = resolveLeadRecordId(previous);
  const nextLeadId = resolveLeadRecordId(next);

  const hasNextContacts = Array.isArray(next.contacts);
  const hasNextJobs = Array.isArray(next.jobs);
  const hasNextActivities = Array.isArray(next.activities);

  if (previousLeadId && nextLeadId && previousLeadId !== nextLeadId) {
    return {
      ...next,
      id: nextLeadId,
      contacts: hasNextContacts ? next.contacts : [],
      jobs: hasNextJobs ? next.jobs : [],
      activities: hasNextActivities ? next.activities : [],
    };
  }

  return {
    ...previous,
    ...next,
    id: nextLeadId || previousLeadId || next.id || previous.id || null,
    contacts: hasNextContacts
      ? next.contacts
      : Array.isArray(previous.contacts)
        ? previous.contacts
        : [],
    jobs: hasNextJobs
      ? next.jobs
      : Array.isArray(previous.jobs)
        ? previous.jobs
        : [],
    activities: hasNextActivities
      ? next.activities
      : Array.isArray(previous.activities)
        ? previous.activities
        : [],
  };
}

function pickFirstNonEmpty(...values) {
  for (const value of values) {
    const normalized = cleanText(value);
    if (normalized) return normalized;
  }
  return "";
}

function parseStringList(value) {
  if (Array.isArray(value)) {
    return value.map((item) => cleanText(item)).filter(Boolean);
  }

  const normalized = cleanText(value);
  if (!normalized) return [];

  if (
    (normalized.startsWith("[") && normalized.endsWith("]")) ||
    (normalized.startsWith("{") && normalized.endsWith("}"))
  ) {
    try {
      const parsed = JSON.parse(normalized);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => cleanText(item)).filter(Boolean);
      }
    } catch {
      // fall back to delimiter parsing
    }
  }

  return normalized
    .split(/[\n,|;/]+/)
    .map((item) => cleanText(item))
    .filter(Boolean);
}

function buildInitials(value, fallback = "L") {
  const initials = cleanText(value)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.replace(/[^A-Za-z0-9]/g, "").charAt(0).toUpperCase())
    .filter(Boolean)
    .join("");

  return initials || fallback;
}

function formatCreatedLabel(value) {
  if (!value) return EMPTY_VALUE;
  const date = parseDateTimeValue(value);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;

  return formatLocalDate(date, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function formatActivityTimestamp(value) {
  return formatRelativeTimeShort(value, { emptyValue: EMPTY_VALUE });
}

function normalizeWebsiteHref(value) {
  return normalizeContactActionHref("website", value);
}

function normalizeCollection(value) {
  if (Array.isArray(value)) {
    return value.filter((item) => item && typeof item === "object");
  }

  const normalized = cleanText(value);
  if (!normalized) return [];

  try {
    const parsed = JSON.parse(normalized);
    return Array.isArray(parsed)
      ? parsed.filter((item) => item && typeof item === "object")
      : [];
  } catch {
    return [];
  }
}

function getRowName(row = {}) {
  return cleanText(
    row.name || row.full_name || row.contact_name || row.hiring_manager_name || row.user_name
  );
}

function normalizePersonRow(row = {}, fallbackRole = "Contact") {
  const name = getRowName(row);
  if (!name) return null;

  return {
    id: row.id ?? row.contact_id ?? row.hiring_manager_id ?? null,
    name,
    role: normalizeContactRoleLabel(row.role || row.title || row.position, {
      contactKind: row.contact_kind,
      source: row.source,
      fallback: fallbackRole,
    }),
    email: cleanText(row.email || row.contact_email),
    phone: cleanText(row.phone || row.contact_phone),
    linkedin_url: cleanText(row.linkedin_url || row.linkedin),
    website_url: cleanText(row.website_url || row.website),
    source: cleanText(row.source),
    contact_kind: cleanText(row.contact_kind),
  };
}

function dedupePeople(rows = []) {
  const seen = new Set();
  const output = [];

  rows
    .filter(Boolean)
    .forEach((row) => {
      const normalized = row.name.toLowerCase();
      const email = cleanText(row.email).toLowerCase();
      const key = row.id ? `id:${row.id}` : email ? `email:${email}` : normalized;
      if (seen.has(key)) return;
      seen.add(key);
      output.push(row);
    });

  return output;
}

function MetaRow({ label, value, link }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "44px 1fr",
        columnGap: 0.65,
        alignItems: "start",
        mb: 0.35,
      }}
    >
      <Typography
        sx={{
          fontSize: 10.5,
          color: MUTED,
          fontFamily: FONT_FAMILY,
        }}
      >
        {label}
      </Typography>

      <Typography
        component={link ? "a" : "span"}
        href={link || undefined}
        target={link ? "_blank" : undefined}
        rel={link ? "noreferrer" : undefined}
        sx={{
          fontSize: 10.5,
          color: link ? PURPLE : TEXT,
          textDecoration: "none",
          fontFamily: FONT_FAMILY,
          wordBreak: "break-word",
        }}
      >
        {value || EMPTY_VALUE}
      </Typography>
    </Box>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 800,
        color: BLUE,
        fontFamily: FONT_FAMILY,
        mb: 0.9,
      }}
    >
      {children}
    </Typography>
  );
}

function InlineLink({ children, onClick }) {
  return (
    <Typography
      onClick={onClick}
      sx={{
        fontSize: 10,
        color: PURPLE,
        fontWeight: 700,
        fontFamily: FONT_FAMILY,
        cursor: typeof onClick === "function" ? "pointer" : "default",
      }}
    >
      {children}
    </Typography>
  );
}

function LeadTableHeader({ columns }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: columns,
        columnGap: 1,
        pb: 0.65,
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <Typography sx={{ fontSize: 9.5, color: MUTED, fontFamily: FONT_FAMILY }}>
        Name
      </Typography>
      <Typography sx={{ fontSize: 9.5, color: MUTED, fontFamily: FONT_FAMILY }}>
        Role
      </Typography>
      <Typography
        sx={{
          fontSize: 9.5,
          color: MUTED,
          fontFamily: FONT_FAMILY,
          textAlign: "right",
        }}
      >
        Actions
      </Typography>
    </Box>
  );
}

function ContactTableHeader() {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1.25fr 1fr auto",
        columnGap: 1,
        pb: 0.65,
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <Typography sx={{ fontSize: 9.5, color: MUTED, fontFamily: FONT_FAMILY }}>
        Name
      </Typography>
      <Typography sx={{ fontSize: 9.5, color: MUTED, fontFamily: FONT_FAMILY }}>
        Role
      </Typography>
      <Typography
        sx={{
          fontSize: 9.5,
          color: MUTED,
          fontFamily: FONT_FAMILY,
          textAlign: "right",
        }}
      >
        Channels
      </Typography>
    </Box>
  );
}

export default function LeadProfileDrawer({
  open,
  onClose,
  lead,
  onShowMore,
}) {
  const [leadProfile, setLeadProfile] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teamLoading, setTeamLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setLeadProfile(null);
      setTeamMembers([]);
      setLoading(false);
      setTeamLoading(false);
      setError("");
      return undefined;
    }

    const baseLead = buildLeadSnapshot(lead);
    const leadId = resolveLeadRecordId(baseLead);

    setLeadProfile(baseLead);
    setTeamMembers([]);
    setError("");

    if (!leadId) {
      setLoading(false);
      setTeamLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    setLoading(true);
    setTeamLoading(true);

    const loadLead = async () => {
      try {
        const [baseResponse, infoResponse, teamRows] = await Promise.all([
          fetch(`${API_BASE}/api/leads/${encodeURIComponent(String(leadId))}`, {
            method: "GET",
            headers: getAuthHeaders(),
            credentials: "include",
            signal: controller.signal,
          }),
          fetch(`${API_BASE}/api/leads/${encodeURIComponent(String(leadId))}/info`, {
            method: "GET",
            headers: getAuthHeaders(),
            credentials: "include",
            signal: controller.signal,
          }).catch(() => null),
          fetchHiringManagers({
            apiBase: API_BASE,
            entityType: "lead",
            entityId: leadId,
            signal: controller.signal,
          }).catch(() => []),
        ]);

        if (!baseResponse.ok) {
          const message = await baseResponse.text().catch(() => "");
          throw new Error(message || `Failed to load lead profile (${baseResponse.status})`);
        }

        const basePayload = await baseResponse.json().catch(() => ({}));
        const baseDetail = buildLeadSnapshot(basePayload?.lead || basePayload || {});

        let infoDetail = {};
        if (infoResponse?.ok) {
          infoDetail = buildLeadSnapshot(await infoResponse.json().catch(() => ({})));
        }

        if (!active) return;

        setLeadProfile((previous) =>
          mergeLeadState(previous, mergeLeadState(baseLead, mergeLeadState(baseDetail, infoDetail)))
        );
        setTeamMembers(Array.isArray(teamRows) ? teamRows : []);
      } catch (loadError) {
        if (!active || loadError?.name === "AbortError") return;
        console.error("Failed to load lead profile drawer:", loadError);
        setError("Unable to load the latest lead quick view data.");
        setLeadProfile((previous) => mergeLeadState(previous, baseLead));
      } finally {
        if (active) {
          setLoading(false);
          setTeamLoading(false);
        }
      }
    };

    loadLead();

    return () => {
      active = false;
      controller.abort();
    };
  }, [lead, open]);

  if (!open) return null;

  const activeLead = mergeLeadState(lead, leadProfile) || buildLeadSnapshot(lead);
  const leadId = resolveLeadRecordId(activeLead);
  const showMore = () => onShowMore?.(activeLead);

  const title = pickFirstNonEmpty(
    activeLead.company_name,
    activeLead.company,
    activeLead.name,
    activeLead.lead_name,
    activeLead.job_title,
    "Lead"
  );
  const subtitleWebsite = pickFirstNonEmpty(
    activeLead.website_url,
    activeLead.website,
    activeLead.company_website,
    activeLead.career_website,
    activeLead.company_career_website
  );
  const websiteHref = normalizeWebsiteHref(subtitleWebsite);
  const websiteLabel = cleanText(subtitleWebsite, EMPTY_VALUE);
  const statusLabel = pickFirstNonEmpty(activeLead.status, "New");
  const createdLabel = formatCreatedLabel(
    activeLead.created_at || activeLead.createdAt || activeLead.date_created
  );
  const sourceLabel = pickFirstNonEmpty(activeLead.source, activeLead.lead_source, EMPTY_VALUE);
  const industryLabel = pickFirstNonEmpty(
    activeLead.industry_sector,
    activeLead.industry,
    activeLead.company_industry,
    activeLead.industry_name,
    EMPTY_VALUE
  );
  const logoSrc = pickFirstNonEmpty(
    activeLead.company_logo,
    activeLead.company_logo_url,
    activeLead.logo,
    activeLead.avatar_url,
    activeLead.profile_image
  );

  const explicitRecruiterNames = [
    ...parseStringList(activeLead.owner_names),
    ...parseStringList(activeLead.team_members),
    ...parseStringList(activeLead.recruiter_names),
    ...parseStringList(activeLead.assigned_team_members),
  ];
  const ownerName = pickFirstNonEmpty(
    activeLead.owner_name,
    activeLead.owner,
    activeLead.assigned_team_member,
    activeLead.recruiter_name,
    activeLead.team_member
  );
  if (ownerName) {
    explicitRecruiterNames.unshift(ownerName);
  }

  const recruiterTeam = dedupePeople([
    ...explicitRecruiterNames.map((name, index) =>
      normalizePersonRow(
        {
          name,
          role: index === 0 && ownerName && areSamePersonNames(name, ownerName) ? "Owner" : "Recruiter",
          contact_kind: "recruiter",
        },
        "Recruiter"
      )
    ),
    ...dedupeHiringManagerRows(teamMembers)
      .filter((row) => isRecruiterContact(row))
      .map((row) => normalizePersonRow(row, "Recruiter")),
  ]).slice(0, 4);

  const recruiterNameSet = new Set(recruiterTeam.map((item) => item?.name?.toLowerCase()).filter(Boolean));

  const contactRows = dedupePeople([
    ...normalizeCollection(activeLead.contacts).map((row) => normalizePersonRow(row, "Contact")),
    ...dedupeHiringManagerRows(teamMembers)
      .filter((row) => {
        const name = cleanText(row?.name).toLowerCase();
        if (!name || recruiterNameSet.has(name)) return false;
        if (isRecruiterContact(row)) return false;
        return isHiringManagerContactRow(row) || cleanText(row?.contact_kind) === "contact";
      })
      .map((row) =>
        normalizePersonRow(
          row,
          isHiringManagerContactRow(row) ? "Hiring Manager" : "Contact"
        )
      ),
    normalizePersonRow(
      {
        name: activeLead.contact_name,
        role: activeLead.contact_role || activeLead.contact_title || "Primary Contact",
        email: activeLead.contact_email,
        phone: activeLead.contact_phone,
        linkedin_url: activeLead.linkedin_url,
      },
      "Primary Contact"
    ),
    normalizePersonRow(
      {
        name: activeLead.company_primary_contact_name,
        role: activeLead.company_primary_contact_role || activeLead.company_primary_contact_title || "Primary Contact",
        email: activeLead.company_primary_contact_email,
        phone: activeLead.company_primary_contact_phone,
      },
      "Primary Contact"
    ),
    normalizePersonRow(
      {
        name: activeLead.hiring_manager_name || activeLead.hiring_manager,
        role: activeLead.hiring_manager_role || activeLead.hiring_manager_title || "Hiring Manager",
        email: activeLead.hiring_manager_email,
        phone: activeLead.hiring_manager_phone,
      },
      "Hiring Manager"
    ),
  ])
    .filter((row) => !recruiterNameSet.has(cleanText(row?.name).toLowerCase()))
    .filter((row) => !isRecruiterContact(row))
    .slice(0, 4);

  const activities = normalizeCollection(activeLead.activities);
  const latestActivity =
    activities[0] ||
    {
      created_at:
        activeLead.last_activity_time ||
        activeLead.updated_at ||
        activeLead.last_activity_date ||
        activeLead.created_at,
      title:
        activeLead.last_activity_title ||
        activeLead.last_activity_type ||
        activeLead.status ||
        "Lead record updated",
      detail:
        activeLead.last_activity_subject ||
        activeLead.last_activity_message ||
        activeLead.last_activity_detail ||
        activeLead.notes ||
        "No recent activity details available yet.",
    };

  const activityTime = formatActivityTimestamp(
    latestActivity?.created_at ||
      latestActivity?.timestamp ||
      latestActivity?.date ||
      latestActivity?.activity_time
  );
  const activityTitle = pickFirstNonEmpty(
    latestActivity?.title,
    latestActivity?.type,
    latestActivity?.subject,
    latestActivity?.action,
    "Lead record updated"
  );
  const activityDetail = pickFirstNonEmpty(
    latestActivity?.detail,
    latestActivity?.message,
    latestActivity?.description,
    latestActivity?.summary,
    "No recent activity details available yet."
  );

  const jobs = normalizeCollection(activeLead.jobs);
  const jobCount = Math.max(
    jobs.length,
    Number(activeLead.jobs_count || activeLead.jobsCount || activeLead.job_openings || 0) || 0,
    cleanText(activeLead.job_title) ? 1 : 0
  );
  const jobPreview = (
    jobs.length
      ? jobs
      : cleanText(activeLead.job_title)
        ? [
            {
              title: activeLead.job_title,
              location: activeLead.location,
            },
          ]
        : []
  )
    .map((row) => ({
      title: pickFirstNonEmpty(row.title, row.job_title, row.name, EMPTY_VALUE),
      location: pickFirstNonEmpty(row.location, row.job_location, row.city),
    }))
    .slice(0, 3);

  const primaryContact = contactRows[0] || null;
  const quickActions = [
    {
      type: "phone",
      href:
        primaryContact?.phone ||
        activeLead.contact_phone ||
        activeLead.phone ||
        activeLead.hiring_manager_phone,
    },
    {
      type: "email",
      href:
        primaryContact?.email ||
        activeLead.contact_email ||
        activeLead.email ||
        activeLead.hiring_manager_email,
    },
    { type: "website", href: subtitleWebsite },
    {
      type: "linkedin",
      href:
        primaryContact?.linkedin_url ||
        activeLead.linkedin_url ||
        activeLead.company_linkedin_url ||
        activeLead.contact_linkedin_url,
    },
  ];

  const headerLeft = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "999px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#EEF2FF",
          color: PURPLE,
          flex: "0 0 auto",
        }}
      >
        <Eye size="16" color={PURPLE} variant="Linear" />
      </Box>

      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 800,
          color: BLUE,
          fontFamily: FONT_FAMILY,
        }}
        noWrap
      >
        Lead Quick View
      </Typography>
    </Box>
  );

  const headerRight = (
    <Box
      component="button"
      type="button"
      onClick={onClose}
      sx={{
        width: 26,
        height: 26,
        p: 0,
        border: 0,
        borderRadius: "999px",
        backgroundColor: "#EEF2FF",
        color: PURPLE,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
    >
      <CloseCircle size="18" color={PURPLE} variant="Bold" style={{ display: "block" }} />
    </Box>
  );

  const footer = (
    <Box sx={{ py: 0.5 }}>
      <Button
        onClick={showMore}
        variant="contained"
        disabled={!leadId && !cleanText(title)}
        sx={{
          display: "block",
          mx: "auto",
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 700,
          fontSize: 10.5,
          fontFamily: FONT_FAMILY,
          backgroundColor: PURPLE,
          boxShadow: "none",
          minWidth: 92,
          px: 2.25,
          py: 0.55,
          "&:hover": {
            backgroundColor: PURPLE_DARK,
            boxShadow: "none",
          },
        }}
      >
        Show more
      </Button>
    </Box>
  );

  return (
    <ReusableRightDrawer
      open={open}
      onClose={onClose}
      id="lead-profile-drawer"
      width={320}
      headerLeft={headerLeft}
      headerRight={headerRight}
      closeVariant="text"
      closeColor={PURPLE}
      paperSx={{
        borderRadius: "14px 0 0 14px",
        boxShadow: "0 18px 45px rgba(2, 6, 23, 0.22)",
      }}
      footer={footer}
    >
      {!lead ? (
        <Alert severity="info">No lead selected.</Alert>
      ) : (
        <Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 1.25,
              alignItems: "start",
              mb: 1.55,
            }}
          >
            <Box sx={{ display: "flex", gap: 1.1, minWidth: 0 }}>
              <Avatar
                src={logoSrc || undefined}
                sx={{
                  width: 38,
                  height: 38,
                  fontSize: 11,
                  fontWeight: 700,
                  background: logoSrc
                    ? "transparent"
                    : "radial-gradient(circle at 30% 30%, #60A5FA, #2563EB)",
                  color: "#FFFFFF",
                  fontFamily: FONT_FAMILY,
                }}
              >
                {buildInitials(title)}
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 13.5,
                    fontWeight: 800,
                    color: TEXT,
                    fontFamily: FONT_FAMILY,
                    lineHeight: 1.25,
                  }}
                  noWrap
                >
                  {title}
                </Typography>

                <Typography
                  component={websiteHref ? "a" : "span"}
                  href={websiteHref || undefined}
                  target={websiteHref ? "_blank" : undefined}
                  rel={websiteHref ? "noreferrer" : undefined}
                  sx={{
                    display: "block",
                    mt: 0.25,
                    fontSize: 10,
                    color: PURPLE,
                    fontFamily: FONT_FAMILY,
                    textDecoration: "none",
                  }}
                  noWrap
                >
                  {websiteLabel}
                </Typography>

                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 0.7,
                    px: 1,
                    minHeight: 20,
                    borderRadius: "999px",
                    ...getSalesStatusChipSx(statusLabel),
                    fontSize: 9.5,
                    fontWeight: 700,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {statusLabel}
                </Box>
              </Box>
            </Box>

            <Box sx={{ minWidth: 94 }}>
              <MetaRow label="Date:" value={createdLabel} />
              <MetaRow label="Source:" value={sourceLabel} />
              <MetaRow label="Industry:" value={industryLabel} />
            </Box>
          </Box>

          <Divider sx={{ borderColor: BORDER, mb: 1.45 }} />

          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              color: BLUE,
              fontFamily: FONT_FAMILY,
              lineHeight: 1.2,
              mb: 0.9,
            }}
          >
            Quick Actions:
          </Typography>

          <Box sx={{ mb: 1.6 }}>
            <InfoActionIcons
              actions={quickActions}
              showDisabled={true}
              showGroupDivider={false}
              preserveOrder={true}
              gap={1}
              iconSize={15}
              variant="filled"
              filledWidth={38}
              filledHeight={28}
            />
          </Box>

          <Divider sx={{ borderColor: BORDER, mb: 1.45 }} />

          <SectionLabel>Recruiter Team:</SectionLabel>
          <LeadTableHeader columns="1.2fr 1fr auto" />

          {recruiterTeam.length ? (
            recruiterTeam.map((member, index) => (
              <Box
                key={`${member.id ?? member.name}-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 1fr auto",
                  columnGap: 1,
                  alignItems: "center",
                  py: 0.82,
                  borderBottom:
                    index === recruiterTeam.length - 1 ? "none" : `1px solid ${BORDER}`,
                }}
              >
                <Typography
                  sx={{ fontSize: 10.5, color: TEXT, fontFamily: FONT_FAMILY }}
                  noWrap
                >
                  {member.name}
                </Typography>
                <Typography
                  sx={{ fontSize: 10, color: "#667085", fontFamily: FONT_FAMILY }}
                  noWrap
                >
                  {member.role}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "flex-end", color: PURPLE }}>
                  <Trash size="15" color={PURPLE} variant="Linear" />
                </Box>
              </Box>
            ))
          ) : (
            <Typography
              sx={{ fontSize: 10, color: MUTED, fontFamily: FONT_FAMILY, py: 1 }}
            >
              {teamLoading ? "Loading recruiter team..." : "No recruiter team members added yet."}
            </Typography>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.45, mb: 1.4 }}>
            <InlineLink onClick={showMore}>Add New</InlineLink>
          </Box>

          <Divider sx={{ borderColor: BORDER, mb: 1.45 }} />

          <SectionLabel>Contacts:</SectionLabel>
          <ContactTableHeader />

          {contactRows.length ? (
            contactRows.map((contact, index) => (
              <Box
                key={`${contact.id ?? contact.name}-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.25fr 1fr auto",
                  columnGap: 1,
                  alignItems: "center",
                  py: 0.82,
                  borderBottom:
                    index === contactRows.length - 1 ? "none" : `1px solid ${BORDER}`,
                }}
              >
                <Typography
                  sx={{ fontSize: 10.5, color: TEXT, fontFamily: FONT_FAMILY }}
                  noWrap
                >
                  {contact.name}
                </Typography>
                <Typography
                  sx={{ fontSize: 10, color: "#667085", fontFamily: FONT_FAMILY }}
                  noWrap
                >
                  {contact.role}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <InfoActionIcons
                    actions={[
                      { type: "linkedin", href: contact.linkedin_url },
                      { type: "email", href: contact.email },
                      { type: "phone", href: contact.phone },
                    ]}
                    showDisabled={true}
                    showGroupDivider={false}
                    preserveOrder={true}
                    gap={0.45}
                    iconSize={16}
                  />
                </Box>
              </Box>
            ))
          ) : (
            <Typography
              sx={{ fontSize: 10, color: MUTED, fontFamily: FONT_FAMILY, py: 1 }}
            >
              {loading ? "Loading contacts..." : "No contacts available yet."}
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.25,
              mt: 0.45,
              mb: 1.4,
            }}
          >
            <InlineLink onClick={showMore}>Add Contact</InlineLink>
            <InlineLink onClick={showMore}>View all</InlineLink>
          </Box>

          <Divider sx={{ borderColor: BORDER, mb: 1.45 }} />

          <SectionLabel>Recent Activity:</SectionLabel>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.55, mb: 0.55 }}>
            <Clock size="14" color={MUTED} variant="Linear" />
            <Typography sx={{ fontSize: 10, color: MUTED, fontFamily: FONT_FAMILY }}>
              {activityTime}
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: 10.5,
              color: TEXT,
              fontFamily: FONT_FAMILY,
              lineHeight: 1.45,
              mb: 0.35,
            }}
          >
            {activityTitle}
          </Typography>
          <Typography
            sx={{
              fontSize: 10.5,
              color: "#667085",
              fontFamily: FONT_FAMILY,
              lineHeight: 1.45,
              mb: 0.55,
            }}
          >
            {activityDetail}
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.4 }}>
            <InlineLink onClick={showMore}>View</InlineLink>
          </Box>

          <Divider sx={{ borderColor: BORDER, mb: 1.45 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.75,
            }}
          >
            <SectionLabel>{`Jobs (${jobCount}):`}</SectionLabel>
            <Box sx={{ mb: 0.9 }}>
              <InlineLink onClick={showMore}>View all</InlineLink>
            </Box>
          </Box>

          <Box sx={{ mb: 0.35 }}>
            {jobPreview.length ? (
              jobPreview.map((jobRow, index) => (
                <Typography
                  key={`${jobRow.title}-${index}`}
                  sx={{
                    fontSize: 10.5,
                    color: TEXT,
                    fontFamily: FONT_FAMILY,
                    lineHeight: 1.5,
                    mb: 0.2,
                  }}
                >
                  {jobRow.title}
                  {jobRow.location ? ` - ${jobRow.location}` : ""}
                </Typography>
              ))
            ) : (
              <Typography
                sx={{ fontSize: 10.5, color: MUTED, fontFamily: FONT_FAMILY }}
              >
                No related jobs available yet.
              </Typography>
            )}
          </Box>

          {(loading || error) ? (
            <Box sx={{ mt: 1.4 }}>
              {error ? <Alert severity="warning">{error}</Alert> : null}
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: error ? 1 : 0 }}>
                  <CircularProgress size={13} />
                  <Typography
                    sx={{ fontSize: 9.5, color: MUTED, fontFamily: FONT_FAMILY }}
                  >
                    Refreshing quick view...
                  </Typography>
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Box>
      )}
    </ReusableRightDrawer>
  );
}
